import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Tooltip,
  Row,
  Upload,
  Input,
  Form,
  Col,
  message,
  Descriptions,
  Spin,
  Space,
  Divider,
  Alert
} from 'antd';
import { FilePdfOutlined, InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import EbzInputNumber from 'components/EbzInputNumber';
import currencyFormat from 'utils/currencyFormat';
import trackingService from 'services/trackingService';
import EVENT_NAME from 'utils/const';
import TellerConset from 'pages/PayoutReportsListPage/TellerConset';
import { BULK_INVOICE_SUMMARY_KEY } from './const';

const FormWrapper = styled(Form)`
  .ant-form-item {
    margin-bottom: 12px;
  }

  .ant-form-item-label {
    padding: 0px;
  }
`;

function InvoiceSummaryModal(props) {
  const { Dragger } = Upload;
  const [invoiceForm] = Form.useForm();
  const {
    handleDrawerClose,
    setInvoiceSummaryLoading,
    payoutAcceptInvoice,
    invoiceSummary,
    invoiceSummaryLoading,
    yearMonth
  } = props;
  const isTellerConsent = useSelector((state) => state.user.extraParam.is_teller_consent);
  const formData = new FormData();

  const submitInvoice = (values) => {
    trackingService.trackEvent(EVENT_NAME.BUTTON_CLICK, { name: 'Invoice Uploaded' });
    setInvoiceSummaryLoading(true);
    const file = values?.uploadInvoice ? values?.uploadInvoice[0]?.originFileObj : null;
    formData.set('partner_payout_list', invoiceSummary?.partner_payout_id);
    formData.set('partner_invoice', file);
    formData.set('partner_invoice_number', values?.invoiceNumber);
    formData.set('invoice_item_name', values?.itemName);
    formData.set('invoice_sac_hsn', values?.invoiceSacHsn);
    formData.set('payout_month', yearMonth);
    payoutAcceptInvoice(formData);
  };
  const handleTrackingService = (event) => {
    trackingService.trackEvent(EVENT_NAME.TEXT_INPUT, { name: event?.target?.name });
  };

  const uploadProps = {
    maxCount: 1,
    listType: 'picture',
    // eslint-disable-next-line react/no-unstable-nested-components
    iconRender: () => {
      return <FilePdfOutlined />;
    },
    beforeUpload: (file) => {
      const isPDF = file.type === 'application/pdf';
      if (!isPDF) {
        message.error(`${file.name} is not a PDF file`);
      }
      return isPDF || Upload.LIST_IGNORE;
    },
    customRequest: async (options) => {
      const uploadService = () => {
        return Promise.resolve();
      };
      // eslint-disable-next-line
      const url = await uploadService();
      options.onProgress({ percent: 0 });
      options.onProgress({ percent: 100 });
      options.onSuccess();
    }
  };
  useEffect(() => {
    invoiceForm.resetFields();
    if (!invoiceSummary) {
      formData.set('partner_payout_list', null);
      formData.set('partner_invoice', null);
      formData.set('partner_invoice_number', null);
      formData.set('invoice_item_name', null);
      formData.set('invoice_sac_hsn', null);
    }
  }, [invoiceSummary]);

  const normFile = (changeObject) => {
    if (Array.isArray(changeObject)) {
      return changeObject;
    }
    return changeObject?.fileList;
  };

  return (
    <>
      {!isTellerConsent && <TellerConset handleConsentCancel={handleDrawerClose} />}
      <Spin spinning={invoiceSummaryLoading}>
        <Row gutter={[15, 20]}>
          <Row style={{ width: '100%' }}>
            <Col span={24}>
              <Alert
                message="Note"
                description={
                  <>
                    * Uploaded invoice is subject to internal verification and further review and
                    shall be processed as per the Partner’s Terms of Use. <br />* Please Upload
                    Invoice in PDF formate and size upto 2 MB only.
                  </>
                }
                type="warning"
              />
            </Col>
          </Row>
          <Row>
            <Col style={{ padding: '10px' }} span={11}>
              <Col span={24}>
                <Descriptions column={1} bordered labelStyle={{ backgroundColor: '#FFFFFF' }}>
                  {BULK_INVOICE_SUMMARY_KEY?.map((invoice) => {
                    return (
                      <Descriptions.Item label={invoice?.label}>
                        {invoice?.prefix} {invoiceSummary[invoice?.key]}
                      </Descriptions.Item>
                    );
                  })}
                </Descriptions>
              </Col>
            </Col>
            <Col span={1}>
              <Divider style={{ height: '390px', margin: '50%' }} type="vertical" />
            </Col>
            <Col span={12}>
              <FormWrapper
                form={invoiceForm}
                name="raise-invoice-form"
                layout="vertical"
                onFinish={submitInvoice}
                size="large"
                footer={[
                  <Space size="small">
                    <Button
                      form="raise-invoice-form"
                      key="submit"
                      htmlType="submit"
                      type="primary"
                      loading={invoiceSummaryLoading}
                    >
                      {invoiceSummaryLoading ? 'Raising Invoice' : 'Raise Invoice'}
                    </Button>
                  </Space>
                ]}
              >
                <Row gutter={[15, 3]}>
                  <Col span={12}>
                    <Form.Item
                      name="invoiceNumber"
                      label={
                        <>
                          <span>Invoice Number</span>
                          <span className="required-mark" />
                        </>
                      }
                      rules={[
                        {
                          required: true,
                          message: 'Please Enter Invoice Number'
                        },
                        {
                          validator(_, value) {
                            if (!/^[A-Za-z0-9\-\\/]+$/.test(value)) {
                              // eslint-disable-next-line prefer-promise-reject-errors
                              return Promise.reject('No Spaces Allowed');
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input name="invoiceNumber" onClick={handleTrackingService} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="itemName"
                      label={
                        <>
                          Item Name{' '}
                          <Tooltip title="Enter the Item Name which is in your Invoice">
                            <InfoCircleOutlined
                              style={{ fontSize: 16, color: 'grey', marginLeft: 8 }}
                            />
                          </Tooltip>
                          <span className="required-mark" />
                        </>
                      }
                      rules={[{ required: true, message: 'Please Enter Item Name' }]}
                    >
                      <Input name="itemName" onClick={handleTrackingService} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="invoiceSacHsn"
                      label={
                        <>
                          <span>
                            SAC / HSN
                            <Tooltip title="Enter the SAC / HSN Number which is in your invoice">
                              <InfoCircleOutlined
                                style={{ fontSize: 16, color: 'grey', marginLeft: 8 }}
                              />
                            </Tooltip>
                          </span>
                          <span className="required-mark" />
                        </>
                      }
                      rules={[
                        {
                          required: true,
                          message: 'Please Enter SAC / HSN No.'
                        }
                      ]}
                    >
                      <Input name="invoiceSacHsn" onClick={handleTrackingService} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="invoiceAmount"
                      rules={[
                        {
                          required: true,
                          message: 'Please Enter Invoice Amount'
                        },
                        {
                          validator(_, value) {
                            if (
                              value !== undefined &&
                              value !== '' &&
                              value !== invoiceSummary?.total_invoice_amount
                            ) {
                              // eslint-disable-next-line prefer-promise-reject-errors
                              return Promise.reject('Please enter a valid Invoice Amount.');
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                      label={<span>Total Invoice Amount</span>}
                    >
                      <EbzInputNumber
                        prefix="₹"
                        name="invoiceAmount"
                        onClick={handleTrackingService}
                        controls={false}
                        style={{ width: '100%' }}
                        formatter={(v) => {
                          if (v !== '') return currencyFormat(v, false, false);
                          return v;
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={
                        <>
                          <span>
                            Upload Invoice (Kindly mention the HSN/SAC code in the invoice)
                          </span>
                          <span className="required-mark" />
                        </>
                      }
                      name="uploadInvoice"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                      rules={[
                        {
                          required: true,
                          message: 'Please upload your invoice pdf'
                        }
                      ]}
                    >
                      <Dragger {...uploadProps}>
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                      </Dragger>
                    </Form.Item>
                  </Col>
                </Row>
                <Row style={{ display: 'flex', justifyContent: 'end', marginTop: '24px' }}>
                  <Form.Item>
                    <Space>
                      <Button
                        size="large"
                        style={{
                          borderColor: 'var(--color-primary)',
                          color: 'var(--color-primary)'
                        }}
                        onClick={() => {
                          handleDrawerClose();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        loading={invoiceSummaryLoading}
                        htmlType="submit"
                        key="manual"
                        type="primary"
                      >
                        Submit
                      </Button>
                    </Space>
                  </Form.Item>
                </Row>
              </FormWrapper>
            </Col>
          </Row>
        </Row>
      </Spin>
    </>
  );
}
InvoiceSummaryModal.propTypes = {
  handleDrawerClose: PropTypes.func,
  setInvoiceSummaryLoading: PropTypes.func,
  payoutAcceptInvoice: PropTypes.func,
  invoiceSummaryLoading: PropTypes.bool,
  selectedInvoice: PropTypes.instanceOf(Object),
  invoiceSummary: PropTypes.instanceOf(Object),
  yearMonth: PropTypes.string.isRequired
};
InvoiceSummaryModal.defaultProps = {
  handleDrawerClose: () => {},
  invoiceSummaryLoading: false,
  setInvoiceSummaryLoading: () => {},
  payoutAcceptInvoice: () => {},
  selectedInvoice: [],
  invoiceSummary: {}
};

export default InvoiceSummaryModal;
